import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as X } from '../../../../assets/icons/close-icon.svg';
import { ReactComponent as Download } from '../../../../assets/icons/download.svg';
import { ReactComponent as Tag } from '../../../../assets/icons/tag.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchEmspTariffFilters,
  fetchEmspTariffs,
  pullEmspTariffs,
  setSortData,
  updateEMSPTariffsFilters,
} from '../../../../redux/slices/OCPI/EMSPTariffsSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiPriceComponent {
  type: string;
  price: number;
  vat?: number;
  step_size?: number;
}

interface OcpiTariffElement {
  price_components?: OcpiPriceComponent[];
  restrictions?: { start_time?: string; end_time?: string };
}

interface OcpiTariff {
  id: string;
  currency?: string;
  type?: string;
  country_code?: string;
  party_id?: string;
  elements?: OcpiTariffElement[];
  start_date_time?: string;
  end_date_time?: string;
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  cpoCountryCode?: string;
  cpoPartyId?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Tariff Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders read-only details of a tariff received from a connected CPO.
 * @param {any} props The props.
 * @param {any} props.tariff The tariff details.
 * @param {any} props.onClose The close callback.
 */
function TariffDetailsModal({
  tariff,
  onClose,
}: Readonly<{
  tariff: OcpiTariff;
  onClose: () => void;
}>) {
  const { t } = useTranslation(['ocpi']);
  const elements: OcpiTariffElement[] = tariff.elements || [];

  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-tariff-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{tariff.id}</h3>
              <p className="cpo-tariff-drilldown-subtitle">
                {tariff.currency || '–'} {tariff.type ? `· ${tariff.type}` : ''}
                {tariff.cpoCountryCode && tariff.cpoPartyId
                  ? ` · Source: ${tariff.cpoCountryCode} / ${tariff.cpoPartyId}`
                  : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <X width={30} height={30} />
            </button>
          </div>

          {/* Elements list */}
          <div className="cpo-drilldown-body">
            {elements.length === 0 ? (
              <div className="cpo-drilldown-empty">
                <Tag height={32} width={32} className="cpo-drilldown-empty-icon" />
                <p>{t('ocpi:noPriceElements')}</p>
              </div>
            ) : (
              <div className="cpo-tariff-element-list">
                {elements.map((element, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <pre key={index} className="cpo-tariff-element-card">
                    {JSON.stringify(element, null, 2)}
                  </pre>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface TariffRow {
  id: string;
  currency: string;
  type: string;
  cpo: string;
  price: string;
  validFrom: string;
  validTo: string;
  elementsCount: number;
  lastUpdated: string;
  _createdAt?: string;
  createdAt: string;
  raw: OcpiTariff;
}

/**
 * EmspTariffsPanel component displays OCPI tariffs received from connected CPOs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the CPO to filter by.
 * @param {any} props.filterPartyId Optional party ID of the CPO to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function EmspTariffsPanel({
  countryCode,
  partyId,
  filterCountryCode,
  filterPartyId,
  refreshTrigger,
}: Readonly<{
  countryCode: string;
  partyId: string;
  filterCountryCode?: string;
  filterPartyId?: string;
  refreshTrigger?: number;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    tariffs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.emspTariffs);
  const [selectedTariff, setSelectedTariff] = useState<OcpiTariff | null>(null);
  const [pulling, setPulling] = useState(false);
  const [pullSuccessMessage, setPullSuccessMessage] = useState<string | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches eMSP tariffs for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(
        fetchEmspTariffs({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
          ...data,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(
      fetchEmspTariffs({
        countryCode,
        partyId,
        counterPartyId: filterPartyId,
        counterCountryCode: filterCountryCode,
      }),
    );
  }, [countryCode, partyId, filterPartyId, filterCountryCode, dispatch, refreshTrigger]);

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof TariffRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {TariffRow} currentRow The current tariff row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: TariffRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:tariffId'),
      accessorKey: 'id' as keyof TariffRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the tariff ID in a code badge.
       * @param {any} row The tariff row.
       */
      customData: (row: TariffRow) => <code className="cpo-tariff-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:sourceCpo'),
      accessorKey: 'cpo' as keyof TariffRow,
      sortAllow: false,
      /**
       * Renders the source CPO badge.
       * @param {any} row The tariff row.
       */
      customData: (row: TariffRow) => <span className="cpo-source-cpo-badge">{row.cpo}</span>,
    },
    {
      label: t('ocpi:currency'),
      accessorKey: 'currency' as keyof TariffRow,
      sortAllow: false,
    },
    // {
    //   label: t('ocpi:type'),
    //   accessorKey: 'type' as keyof TariffRow,
    //   sortAllow: false,
    // },
    {
      label: t('ocpi:price'),
      accessorKey: 'price' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:validFrom'),
      accessorKey: 'validFrom' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:validTo'),
      accessorKey: 'validTo' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:elements'),
      accessorKey: 'elementsCount' as keyof TariffRow,
      sortAllow: false,
      /**
       * Renders the price elements count badge.
       * @param {any} row The tariff row.
       */
      customData: (row: TariffRow) => (
        <span className="cpo-tariff-elements-badge">
          {row.elementsCount}{' '}
          {t(row.elementsCount !== 1 ? 'ocpi:elementCountPlural' : 'ocpi:elementCountSingular')}
        </span>
      ),
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof TariffRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof TariffRow,
      sortAllow: false,
    },
  ];

  /**
   * Summarizes the first price component across a tariff's elements.
   * @param {OcpiTariff} tariff The tariff.
   * @returns {string} A "TYPE price/unit currency" summary, or '–' if none exist.
   */
  const summarizePrice = (tariff: OcpiTariff): string => {
    const component = tariff.elements?.[0]?.price_components?.[0];
    if (!component) return '–';
    return `${component.type} ${component.price} ${tariff.currency || ''}`.trim();
  };

  const tableData: TariffRow[] = tariffs.map((tariff: OcpiTariff) => {
    const tariffId = tariff.id || tariff._objectId || '';
    const dateString = tariff.last_updated || tariff._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const sourceCpo =
      tariff.cpoCountryCode && tariff.cpoPartyId
        ? `${tariff.cpoCountryCode} / ${tariff.cpoPartyId}`
        : '–';
    const createdAt =
      tariff._createdAt ||
      tariff.createdAt ||
      (tariff as any).created_at ||
      (tariff as any).created ||
      '';

    return {
      id: tariffId,
      currency: tariff.currency || '–',
      type: tariff.type || '–',
      cpo: sourceCpo,
      price: summarizePrice(tariff),
      validFrom: tariff.start_date_time ? new Date(tariff.start_date_time).toLocaleString() : '–',
      validTo: tariff.end_date_time ? new Date(tariff.end_date_time).toLocaleString() : '–',
      elementsCount: tariff.elements?.length ?? 0,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: tariff,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField, sortBy });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Manually pulls tariffs from the connected CPO for this eMSP role. Shows a success
   * modal and refreshes the table on success; errors (offline, no response, or a
   * partner-side failure) are surfaced via the shared notification toast.
   */
  const handlePull = async () => {
    setPulling(true);
    try {
      const result = await dispatch(
        pullEmspTariffs({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
        }),
      ).unwrap();
      setPullSuccessMessage(result.message);
      handleRefresh();
    } catch {
      // Error notification already dispatched by the pullEmspTariffs thunk.
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="cpo-tariffs-panel">
      {/* ── Top bar ── */}
      <div className="cpo-tariffs-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:receivedCpoTariffs')}</h3>
          <p className="cpo-panel-subtitle">
            {tariffs.length > 0
              ? `${tariffs.length} ${t(
                  tariffs.length !== 1 ? 'ocpi:tariffCountPlural' : 'ocpi:tariffCountSingular',
                )} received from connected CPOs`
              : t('ocpi:noTariffsReceived')}
          </p>
        </div>
      </div>

      {/* ── Tariffs Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateEMSPTariffsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchEmspTariffFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedTariff(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
        rightTopContent={
          <MyButton
            label={pulling ? t('ocpi:pullingTariffs') : t('ocpi:pullTariffs')}
            buttonType="primary"
            svgIcon={<Download height={14} width={14} />}
            onClickFunc={handlePull}
            disableButton={pulling}
          />
        }
      />

      {/* ── Drill-down modal ── */}
      {selectedTariff && (
        <TariffDetailsModal tariff={selectedTariff} onClose={() => setSelectedTariff(null)} />
      )}

      {/* ── Pull success modal ── */}
      <ModalBox
        open={Boolean(pullSuccessMessage)}
        header={false}
        notification
        title={null}
        closeModal={() => setPullSuccessMessage(null)}
        content={
          <SuccessNotification
            title={t('ocpi:tariffsPulledSuccessfully')}
            description={pullSuccessMessage || ''}
            onNotificationClose={() => setPullSuccessMessage(null)}
          />
        }
      />
    </div>
  );
}

EmspTariffsPanel.defaultProps = {
  filterCountryCode: undefined,
  filterPartyId: undefined,
  refreshTrigger: undefined,
};
